import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import { Dialog } from "../../components/ui/overlay";

/** Your own Ask usage; refreshed after every ask. */
export const ASK_USAGE_KEY = ["ask-usage"];
export const useAskUsage = (enabled: boolean) => useQuery({ queryKey: ASK_USAGE_KEY, queryFn: api.askUsage, enabled });

/** "410k" for 410,000: one rough number is easier to read than four exact ones. */
export const formatTokens = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${Math.round(n / 1_000)}k` : String(n);

/** Your own asks and tokens this month and last (account menu). No dollars: prices aren't set yet. */
export function AskUsageDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const usage = useAskUsage(open);
  const month = (offset: number) => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1)).toLocaleString(undefined, { month: "long", timeZone: "UTC" });
  };
  const rows = usage.data && [
    { label: `This month (${month(0)})`, ...usage.data.thisMonth },
    { label: `Last month (${month(1)})`, ...usage.data.lastMonth },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Ask usage" className="w-full max-w-md p-6">
      <h2 className="flex items-center gap-2 text-base font-semibold"><Sparkles className="size-4 text-accent" /> Ask usage</h2>
      <p className="mt-1 text-sm text-muted">The questions you've asked, and the tokens the model used to answer them.</p>
      {usage.error && <p className="mt-4 text-sm text-bad">{(usage.error as Error).message}</p>}
      {rows && (
        <table className="mt-4 w-full text-sm">
          <thead className="text-left text-xs text-subtle">
            <tr><th className="pb-1 font-medium" /><th className="pb-1 text-right font-medium">Asks</th><th className="pb-1 text-right font-medium">Tokens</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-t border-border">
                <td className="py-2">{r.label}</td>
                <td className="py-2 text-right tabular-nums">{r.asks.toLocaleString()}</td>
                <td className="py-2 text-right tabular-nums" title={r.tokens.toLocaleString()}>{formatTokens(r.tokens)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="mt-4 text-[11px] text-subtle">Months are calendar months in UTC.</p>
    </Dialog>
  );
}
